/**
 * Shopper dismiss (x) for the announcement bar.
 *
 * Loaded as a plain `defer` script from sections/header-announcements.liquid,
 * and only when the bar is dismissible at all -- either its "Let shoppers
 * dismiss the bar" setting is on, or the campaign currently taking the bar over
 * opted in on its own (`dismissible` on the discount_campaign metaobject, which
 * reaches the section as `data-bc-dismissible` on the campaign's slide).
 * Deliberately NOT a module: it wires one click handler and nothing to import.
 *
 * The HIDING half of this feature is not here -- an already-dismissed bar is
 * hidden during parse by the inline gate in that section, before first paint.
 * This file only handles the click that creates the dismissal.
 *
 * The remembered value is the bar's CONTENT KEY, not a boolean:
 *   - a campaign takeover  => "<campaign handle>|<md5 of its message>"
 *     (stamped on the slide by snippets/bc-campaign-announcement.liquid)
 *   - static blocks        => md5 of the blocks' concatenated plain texts
 *     (stamped on the <aside> by the section)
 * so a dismissal silences exactly what was on screen and nothing else. Edit the
 * announcement, or start a new campaign, and the bar returns by itself.
 *
 * Storage is a JSON ARRAY of up to 10 such keys, newest last, rather than a
 * single value. A shopper who switches locale (the announcement is translated,
 * so its key changes) or a store running two announcement bars would otherwise
 * ping-pong: each dismissal evicting the other's memory and each bar coming
 * back. Ten is far more than either case needs and keeps the entry tiny; the
 * oldest key falls off the front.
 *
 * Every localStorage access is wrapped: it throws outright in Safari's private
 * mode and wherever site data is blocked. A dismissal that cannot be stored
 * still hides the bar for this page view -- it just will not be remembered.
 *
 * In the THEME EDITOR the bar is hidden on click (so the merchant sees what the
 * button does) but nothing is written: the editor preview runs on the storefront
 * origin and shares its localStorage, so a merchant trying the button out would
 * otherwise silently hide the real bar on their own browser.
 */

(function () {
  // Editor section re-renders re-run this file; one document-level listener is
  // all we ever want.
  if (window.__bcAnnouncementDismissWired) return;
  window.__bcAnnouncementDismissWired = true;

  const STORAGE_KEY = 'bc-announcement-dismissed';
  const MAX_REMEMBERED = 10;

  /**
   * The key for what this bar is currently saying. A campaign takeover's slide
   * carries its own; otherwise the static blocks' key on the <aside> stands.
   * @param {HTMLElement} bar
   * @returns {string}
   */
  function contentKey(bar) {
    const slide = bar.querySelector('[data-bc-dismiss-key]');
    return (slide && slide.getAttribute('data-bc-dismiss-key')) || bar.getAttribute('data-bc-announcement-key') || '';
  }

  /**
   * Appends a key to the remembered list, de-duplicated and capped.
   * @param {string} key
   */
  function remember(key) {
    let keys = [];

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) keys = parsed.filter((entry) => typeof entry === 'string');
    } catch (e) {
      // Unreadable or unparseable (blocked storage, or a value written by
      // something else under this name). Start the list over rather than lose
      // this dismissal.
    }

    keys = keys.filter((entry) => entry !== key);
    keys.push(key);
    if (keys.length > MAX_REMEMBERED) keys = keys.slice(-MAX_REMEMBERED);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
    } catch (e) {
      // Storage unavailable. The bar still hides for this page view.
    }
  }

  /**
   * Hides a bar. `hidden` plus an inline `display` so the bar goes away even if
   * assets/bc-announcement-dismiss.css never loaded (the <aside> is `.section`,
   * i.e. `display: grid`, which beats the UA's `[hidden]` rule on its own), and
   * `pause()` so a multi-slide bar stops cycling an invisible slideshow forever.
   * @param {HTMLElement} bar
   */
  function hide(bar) {
    bar.hidden = true;
    bar.style.display = 'none';

    // `pause()` is upstream AnnouncementBar's own method; the optional call
    // covers a bar with no slider and an element not yet upgraded.
    const slider = /** @type {any} */ (bar.querySelector('announcement-bar-component'));
    slider?.pause?.();
  }

  /**
   * Republishes `--header-group-height`.
   *
   * header.js keeps that custom property fresh with a ResizeObserver over the
   * header group's children, but its callback sums only the entries IT WAS
   * HANDED -- and hiding the bar changes exactly one child, so that callback
   * would set the whole group's height to the announcement section's new 0px.
   * Sticky offsets (bc-product-toc, bc-product-tabs, scroll-margin) read it.
   *
   * This recomputes the real total, mirroring calculateHeaderGroupHeight() in
   * utilities.js.
   */
  function syncHeaderGroupHeight() {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return;

    const header = document.querySelector('#header-component');
    let total = 0;
    for (const element of headerGroup.children) {
      if (element instanceof HTMLElement) total += element.offsetHeight;
    }

    // A transparent header is absolutely positioned, so the walk above counts
    // its section wrapper as 0px and its height has to be added back.
    if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      total += header.offsetHeight;
    }

    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  }

  /**
   * Corrects `--header-group-height` after a dismissal, twice over.
   *
   * The primary path is a throwaway ResizeObserver created HERE, at dismiss
   * time: observers are broadcast in creation order within one delivery cycle,
   * so this one -- created long after header.js's -- runs immediately after it,
   * still before paint. The value is wrong for no rendered frame at all.
   *
   * The double rAF is the fallback, for anything that keeps the observer from
   * firing. It has to be double: resize observations are delivered AFTER a
   * frame's animation-frame callbacks, so a single rAF would write the correct
   * value and then watch header.js overwrite it.
   */
  function scheduleHeaderResync() {
    const headerGroup = document.querySelector('#header-group');

    if (headerGroup && typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        observer.disconnect();
        syncHeaderGroupHeight();
      });
      for (const element of headerGroup.children) {
        if (element instanceof HTMLElement) observer.observe(element);
      }
    }

    requestAnimationFrame(() => requestAnimationFrame(syncHeaderGroupHeight));
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-bc-announcement-dismiss]');
    if (!button) return;

    const bar = button.closest('.announcement-bar');
    if (!(bar instanceof HTMLElement)) return;

    const key = contentKey(bar);
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    if (key && !inEditor) remember(key);

    hide(bar);
    scheduleHeaderResync();
  });
})();
